import { useSyncExternalStore } from "react";
import { keepFor, NIGHT_QUERY, resolveSheetMode, SHEET_STORAGE_KEY, type SheetMode } from "@/lib/sheet-mode";

/*
  The sheet's day or night, as one store for every toggle on the page (the
  nav, the menu and the footer each carry one). The truth is `data-sheet` on
  <html>, which the boot script has already set; this keeps it right when the
  device's scheme changes, when another tab picks, and when the reader does.
*/

const listeners = new Set<() => void>();

const media = () => (typeof window.matchMedia === "function" ? window.matchMedia(NIGHT_QUERY) : null);
const deviceNight = () => media()?.matches ?? false;

function stored(): string | null {
  try {
    return localStorage.getItem(SHEET_STORAGE_KEY);
  } catch {
    // Safari private mode throws on access rather than returning null.
    return null;
  }
}

function paint(mode: SheetMode) {
  document.documentElement.dataset.sheet = mode;
  listeners.forEach((listener) => listener());
}

const follow = () => paint(resolveSheetMode(stored(), deviceNight()));
const onStorage = (e: StorageEvent) => {
  if (e.key === SHEET_STORAGE_KEY) follow();
};

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1) {
    media()?.addEventListener("change", follow);
    window.addEventListener("storage", onStorage);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      media()?.removeEventListener("change", follow);
      window.removeEventListener("storage", onStorage);
    }
  };
}

const read = (): SheetMode => resolveSheetMode(document.documentElement.dataset.sheet, deviceNight());

/** Turning the sheet over: the new side comes down over the old, unless motion is reduced. */
function turn(mode: SheetMode) {
  const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  if (still || typeof document.startViewTransition !== "function") paint(mode);
  else document.startViewTransition(() => paint(mode));
}

export function useSheetMode(): [SheetMode, (mode: SheetMode) => void] {
  const mode = useSyncExternalStore(subscribe, read, () => "day" as const);
  const pick = (next: SheetMode) => {
    if (next === mode) return;
    const keep = keepFor(next, deviceNight());
    try {
      if (keep) localStorage.setItem(SHEET_STORAGE_KEY, keep);
      else localStorage.removeItem(SHEET_STORAGE_KEY);
    } catch {
      // Unwritable storage: the pick holds for this page.
    }
    turn(next);
  };
  return [mode, pick];
}
